import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { fetchEffectivePermissions, clearPermissionRegistryCache } from './platformPermissionsApi';
import type { PlatformAccessLevel } from './platformAccessLevel';
import { logout } from '../auth/authSlice';

export const loadEffectivePlatformPermissions = createAsyncThunk(
  'platformPermissions/loadEffective',
  async () => fetchEffectivePermissions(),
  {
    condition: (_, { getState }) => {
      const status = (getState() as RootState).platformPermissions.status;
      return status === 'idle' || status === 'failed';
    },
  },
);

export const refreshEffectivePlatformPermissions = createAsyncThunk(
  'platformPermissions/refreshEffective',
  async () => fetchEffectivePermissions(),
);

interface PlatformPermissionsState {
  effective: Record<string, PlatformAccessLevel> | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
}

const initialState: PlatformPermissionsState = {
  effective: null,
  status: 'idle',
};

const platformPermissionsSlice = createSlice({
  name: 'platformPermissions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadEffectivePlatformPermissions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadEffectivePlatformPermissions.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.effective = action.payload;
      })
      .addCase(loadEffectivePlatformPermissions.rejected, (state) => {
        state.status = 'failed';
        state.effective = null;
      })
      .addCase(refreshEffectivePlatformPermissions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(refreshEffectivePlatformPermissions.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.effective = action.payload;
      })
      .addCase(refreshEffectivePlatformPermissions.rejected, (state) => {
        state.status = 'failed';
        state.effective = null;
      })
      .addCase(logout, () => {
        clearPermissionRegistryCache();
        return { ...initialState };
      });
  },
});

export const selectEffectivePlatformPermissions = (state: RootState) =>
  state.platformPermissions.effective;
export const selectPlatformPermissionsStatus = (state: RootState) =>
  state.platformPermissions.status;

export default platformPermissionsSlice.reducer;
